/**
 * ContentItemMetadata Component
 * Displays metadata information for a content item in a panel format
 */

import { Link } from 'react-router-dom'
import {
  Calendar,
  Clock,
  User,
  Folder,
  Eye,
  TrendingUp,
  History,
  Hash,
  ExternalLink,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { FluentCard } from '@/components/layout/page-header'
import type { ContentItemDetail, ContentItemUsageStats } from '@/lib/api/types'

// ============================================
// Types
// ============================================

interface ContentItemMetadataProps {
  item: ContentItemDetail
  usageStats?: ContentItemUsageStats
  className?: string
  showUsageStats?: boolean
}

interface MetadataRowProps {
  icon: React.ElementType
  label: string
  value: React.ReactNode
  className?: string
}

// ============================================
// MetadataRow Component
// ============================================

function MetadataRow({ icon: Icon, label, value, className }: MetadataRowProps) {
  return (
    <div className={cn('p-3 rounded-xl bg-neutral-50 dark:bg-neutral-800/50', className)}>
      <div className="flex items-center gap-2 mb-1">
        <Icon className="h-3.5 w-3.5 text-neutral-400" />
        <p className="text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
          {label}
        </p>
      </div>
      <div className="text-sm text-neutral-900 dark:text-white">{value}</div>
    </div>
  )
}

// ============================================
// ContentItemMetadata Component
// ============================================

export function ContentItemMetadata({
  item,
  usageStats,
  className,
  showUsageStats = true,
}: ContentItemMetadataProps) {
  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })
  }

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    })
  }

  const formatRelativeTime = (dateString: string) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffInMs = now.getTime() - date.getTime()
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24))

    if (diffInDays === 0) {
      const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60))
      if (diffInHours === 0) {
        const diffInMinutes = Math.floor(diffInMs / (1000 * 60))
        if (diffInMinutes < 1) return 'Just now'
        return `${diffInMinutes} minute${diffInMinutes > 1 ? 's' : ''} ago`
      }
      return `${diffInHours} hour${diffInHours > 1 ? 's' : ''} ago`
    }
    if (diffInDays === 1) return 'Yesterday'
    if (diffInDays < 7) return `${diffInDays} days ago`
    if (diffInDays < 30) return `${Math.floor(diffInDays / 7)} week${Math.floor(diffInDays / 7) > 1 ? 's' : ''} ago`
    return formatDate(dateString)
  }

  return (
    <div className={cn('space-y-6', className)}>
      {/* Information Card */}
      <FluentCard>
        <h2 className="text-lg font-semibold text-neutral-900 dark:text-white mb-4">Information</h2>

        <div className="space-y-3">
          <MetadataRow
            icon={Calendar}
            label="Created"
            value={
              <div>
                <p>{formatDate(item.createdAt)}</p>
                <p className="text-xs text-neutral-500 dark:text-neutral-400">
                  {formatRelativeTime(item.createdAt)}
                </p>
              </div>
            }
          />

          <MetadataRow
            icon={Clock}
            label="Last Updated"
            value={
              <div>
                <p>{formatDate(item.updatedAt)}</p>
                <p className="text-xs text-neutral-500 dark:text-neutral-400">
                  {formatRelativeTime(item.updatedAt)}
                </p>
              </div>
            }
          />

          <MetadataRow
            icon={User}
            label="Created By"
            value={item.createdByName || item.createdBy}
          />

          {item.folder && (
            <MetadataRow
              icon={Folder}
              label="Location"
              value={
                <Link
                  to={`/content-hub/folder/${item.folder.id}`}
                  className="inline-flex items-center gap-1.5 text-blue-600 dark:text-blue-400 hover:underline"
                >
                  {item.folder.name}
                  <ExternalLink className="h-3 w-3" />
                </Link>
              }
            />
          )}

          {item.folderPath && !item.folder && (
            <MetadataRow icon={Folder} label="Path" value={item.folderPath || 'Root'} />
          )}

          <MetadataRow
            icon={History}
            label="Version"
            value={
              <span className="inline-flex items-center gap-2">
                v{item.version}
                {item.version > 1 && (
                  <span className="text-xs text-neutral-500 dark:text-neutral-400">
                    ({item.version - 1} revision{item.version > 2 ? 's' : ''})
                  </span>
                )}
              </span>
            }
          />

          <MetadataRow
            icon={Hash}
            label="ID"
            value={
              <code className="text-xs bg-neutral-100 dark:bg-neutral-800 px-2 py-0.5 rounded">
                {item.id}
              </code>
            }
          />
        </div>
      </FluentCard>

      {/* Usage Statistics Card */}
      {showUsageStats && (
        <FluentCard>
          <h2 className="text-lg font-semibold text-neutral-900 dark:text-white mb-4">
            Usage Statistics
          </h2>

          <div className="space-y-3">
            <MetadataRow
              icon={Eye}
              label="Total Usage"
              value={
                <div className="flex items-center gap-2">
                  <span className="text-lg font-semibold">{item.usageCount}</span>
                  <span className="text-sm text-neutral-500 dark:text-neutral-400">
                    time{item.usageCount !== 1 ? 's' : ''}
                  </span>
                </div>
              }
            />

            {item.lastUsedAt && (
              <MetadataRow
                icon={Clock}
                label="Last Used"
                value={
                  <div>
                    <p>{formatDateTime(item.lastUsedAt)}</p>
                    <p className="text-xs text-neutral-500 dark:text-neutral-400">
                      {formatRelativeTime(item.lastUsedAt)}
                    </p>
                  </div>
                }
              />
            )}

            {usageStats && (
              <>
                <MetadataRow
                  icon={TrendingUp}
                  label="This Month"
                  value={
                    <div className="flex items-center gap-2">
                      <span className="text-lg font-semibold">{usageStats.usagesThisMonth}</span>
                      {usageStats.trend !== 0 && (
                        <span
                          className={cn(
                            'text-xs px-2 py-0.5 rounded-full font-medium',
                            usageStats.trend > 0
                              ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200'
                              : 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200'
                          )}
                        >
                          {usageStats.trend > 0 ? '+' : ''}
                          {usageStats.trend}%
                        </span>
                      )}
                    </div>
                  }
                />

                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 rounded-xl bg-neutral-50 dark:bg-neutral-800/50 text-center">
                    <p className="text-2xl font-semibold text-neutral-900 dark:text-white">
                      {usageStats.uniqueTemplates}
                    </p>
                    <p className="text-xs text-neutral-500 dark:text-neutral-400">Templates</p>
                  </div>
                  <div className="p-3 rounded-xl bg-neutral-50 dark:bg-neutral-800/50 text-center">
                    <p className="text-2xl font-semibold text-neutral-900 dark:text-white">
                      {usageStats.uniqueUsers}
                    </p>
                    <p className="text-xs text-neutral-500 dark:text-neutral-400">Users</p>
                  </div>
                </div>
              </>
            )}
          </div>
        </FluentCard>
      )}

      {/* Status Card */}
      <FluentCard className={cn('border-l-4', item.isActive ? 'border-l-green-500' : 'border-l-amber-500')}>
        <div className="flex items-center gap-3">
          <div
            className={cn(
              'w-2.5 h-2.5 rounded-full',
              item.isActive ? 'bg-green-500' : 'bg-amber-500'
            )}
          />
          <div>
            <p className="font-medium text-neutral-900 dark:text-white">
              {item.isActive ? 'Active' : 'Inactive'}
            </p>
            <p className="text-xs text-neutral-500 dark:text-neutral-400">
              {item.isActive
                ? 'This content item is available for use'
                : 'This content item is disabled'}
            </p>
          </div>
        </div>
      </FluentCard>
    </div>
  )
}

export default ContentItemMetadata
